// src/router/index.js
import React, { Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { PERMISSION_RESOURCES, PERMISSION_ACTIONS } from '../constants/permissionConstants';

// Layouts
import AuthLayout from '../components/layout/AuthLayout';
import MagicDashboardLayout from '../components/layout/MagicDashboardLayout';


// Auth routes
import ForgotPassword from '../pages/auth/ForgotPassword';
import Login from '../pages/auth/Login';
import Register from '../pages/auth/Register';
import ResetPassword from '../pages/auth/ResetPassword';

// Dashboard
// import Dashboard from '../pages/dashboard/Dashboard';

// Projects
import ProjectCalendar from '../components/projects/ProjectCalendar';
import ProjectKanban from '../components/projects/ProjectKanban';
import ProjectBudget from '../pages/projects/ProjectBudget';
import ProjectCreateEdit from '../pages/projects/ProjectCreateEdit';
import ProjectDocuments from '../pages/projects/ProjectDocuments';
import Projects from '../pages/projects/Projects';
import ProjectTeam from '../pages/projects/ProjectTeam';
import SocialGrid from '../components/features/grid/SocialGrid';

// Project Templates
import ProjectTemplates from '../pages/ProjectTemplates';
import ProjectTemplateBuilder from '../components/projects/templates/ProjectTemplateBuilder';

// Tasks

// Settings
import Settings from '../pages/settings/Settings';
import TaxConfiguration from '../pages/settings/TaxConfiguration';
import CompanySettings from '../pages/settings/CompanySettings';
import QuoteTemplates from '../pages/settings/QuoteTemplates';


// Clients
import Clients from '../pages/clients/Clients';

// Brands
import BrandDetail from '../pages/brands/BrandDetail';
import BrandForm from '../pages/brands/BrandForm';
import Brands from '../pages/brands/Brands';

// Campaigns
import CampaignDetail from '../pages/campaigns/CampaignDetail';
import CampaignForm from '../pages/campaigns/CampaignForm';
import Campaigns from '../pages/campaigns/Campaigns';

// Workflows
import WorkflowCreate from '../pages/workflows/WorkflowCreate';
import WorkflowDetail from '../pages/workflows/WorkflowDetail';
import WorkflowInstances from '../pages/workflows/WorkflowInstances';
import WorkflowTemplateForm from '../pages/workflows/WorkflowTemplateForm';
import WorkflowTemplates from '../pages/workflows/WorkflowTemplates';

// Automation
import AutomationDashboard from '../pages/automation/AutomationDashboard';
import WorkflowEditor from '../pages/automation/WorkflowEditor';
import AutomationWorkflowDetail from '../pages/automation/WorkflowDetail';
import ExecutionDetail from '../pages/automation/ExecutionDetail';

// Analytics
import AnalyticsRoutes from '../routes/analyticsRoutes';
import TimeTrackingAnalytics from '../pages/timeTracking/TimeTrackingAnalytics';

// Import additional route groups
import clientRoutes from '../routes/clientRoutes';
import contentRoutes from '../routes/contentRoutes';
import formRoutes from '../routes/formRoutes';
import invoiceRoutes from '../routes/invoiceRoutes';
import expenseRoutes from '../routes/expenseRoutes';
import userRoutes from '../routes/userRoutes';
import vendorRoutes from '../routes/vendorRoutes';
import hrRoutes from '../routes/hrRoutes';
import { payrollRoutes } from '../routes/payrollRoutes';
import leadRoutes from '../routes/leadRoutes';
import sostacRoutes from '../routes/sostacRoutes';
import pdfRoutes from '../routes/pdfRoutes';
import pricingRoutes from '../routes/pricingRoutes';
import processFlowRoutes from '../routes/processFlowRoutes';

// Auth middleware
import ProtectedRoute from '../components/auth/ProtectedRoute';
import ProjectContainer from '../components/projects/ProjectContainer';
import ProjectDetailOverview from '../components/projects/ProjectDetailOverview';
import CreateRolePage from '../pages/admin/CreateRolePage';
import EditRolePage from '../pages/admin/EditRolePage';
import RolesPage from '../pages/admin/RolePage';
import Dashboard from '../pages/dashboard/Dashboard';
import ProjectTasksPage from '../pages/projects/ProjectTaskPage';
import DrawerTriggerPage from '../pages/tasks/DrawerTriggerPage';
import TasksPage from '../pages/tasks/TasksPage';
import UnifiedTaskList from '../components/tasks/UnifiedTaskList';
import AghrbaDesignSystem from '../AghrbaDesignSystem';
import AghrbaLightDesignSystem from '../AghrbaLightDesignSystem';

// Calendar
import GlobalTaskCalendar from '../pages/calendar/GlobalTaskCalendar';

// Public form components
import PublicFormView from '../pages/forms/public/PublicFormView';
import FormPasswordProtection from '../components/forms/renderer/FormPasswordProtection';
import ThankYouPage from '../pages/forms/public/ThankYouPage';
// import TaskList from '../pages/tasks/TasksList';


// Define the router
const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MagicDashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      // Redirect root to dashboard
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // Dashboard
      { path: 'dashboard', element: <Dashboard /> },

      // Settings
      { path: 'settings', element: <Settings /> },
      { 
        path: 'settings/tax-configuration', 
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'tax_config', action: 'read' }}>
            <TaxConfiguration />
          </ProtectedRoute>
        ) 
      },
      { 
        path: 'settings/company', 
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'settings', action: 'update' }}>
            <CompanySettings />
          </ProtectedRoute>
        ) 
      },
      { 
        path: 'settings/quote-templates', 
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'quote_templates', action: 'read' }}>
            <QuoteTemplates />
          </ProtectedRoute>
        ) 
      },
      { path: 'design', element: <AghrbaDesignSystem /> },
      { path: 'designLight', element: <AghrbaLightDesignSystem /> },

      // Projects section
      {
        path: 'projects',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'read' }}>
                <Projects />
              </ProtectedRoute>
            )
          },
          {
            path: 'create',
            element: (
              <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'create' }}>
                <ProjectCreateEdit />
              </ProtectedRoute>
            )
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'read' }}>
                <ProjectContainer />
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'read' }}>
                    <ProjectDetailOverview />
                  </ProtectedRoute>
                )
              },
              {
                path: 'edit',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'update' }}>
                    <ProjectCreateEdit />
                  </ProtectedRoute>
                )
              },
              {
                path: 'tasks',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'read' }}>
                    <ProjectTasksPage />
                  </ProtectedRoute>
                )
              },
              {
                path: 'kanban',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'read' }}>
                    <ProjectKanban />
                  </ProtectedRoute>
                )
              },
              {
                path: 'grid',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'read' }}>
                    <SocialGrid />
                  </ProtectedRoute>
                )
              },
              {
                path: 'calendar',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'read' }}>
                    <ProjectCalendar />
                  </ProtectedRoute>
                )
              },
              {
                path: 'team',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'read' }}>
                    <ProjectTeam />
                  </ProtectedRoute>
                )
              },
              {
                path: 'documents',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'read' }}>
                    <ProjectDocuments />
                  </ProtectedRoute>
                )
              },
              {
                path: 'budget',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'read' }}>
                    <ProjectBudget />
                  </ProtectedRoute>
                )
              },
              {
                path: 'automation',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'automation', action: 'read' }}>
                    <AutomationDashboard />
                  </ProtectedRoute>
                )
              },
              {
                path: 'automation/workflows/new',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'automation', action: 'create' }}>
                    <WorkflowEditor />
                  </ProtectedRoute>
                )
              },
              {
                path: 'automation/workflows/:workflowId/edit',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'automation', action: 'update' }}>
                    <WorkflowEditor />
                  </ProtectedRoute>
                )
              },
              {
                path: 'automation/workflows/:workflowId',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'automation', action: 'read' }}>
                    <AutomationWorkflowDetail />
                  </ProtectedRoute>
                )
              },
              {
                path: 'automation/executions/:executionId',
                element: (
                  <ProtectedRoute requiredPermission={{ resource: 'automation', action: 'read' }}>
                    <ExecutionDetail />
                  </ProtectedRoute>
                )
              }
            ]
          },
          // Redirect /projects/* to /projects if no match
          { path: '*', element: <Navigate to="/projects" replace /> }
        ]
      },

      // Project Templates section
      {
        path: 'project-templates',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute requiredPermission={{ resource: 'project_templates', action: 'read' }}>
                <ProjectTemplates />
              </ProtectedRoute>
            )
          },
          {
            path: 'create',
            element: (
              <ProtectedRoute requiredPermission={{ resource: 'project_templates', action: 'create' }}>
                <ProjectTemplateBuilder />
              </ProtectedRoute>
            )
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute requiredPermission={{ resource: 'project_templates', action: 'update' }}>
                <ProjectTemplateBuilder />
              </ProtectedRoute>
            )
          },
          // Redirect /project-templates/* to /project-templates if no match
          { path: '*', element: <Navigate to="/project-templates" replace /> }
        ]
      },

      // Global Calendar
      {
        path: 'calendar',
        element: (
          <ProtectedRoute requiredPermission={{ resource: PERMISSION_RESOURCES.GLOBAL_CALENDAR, action: PERMISSION_ACTIONS.READ }}>
            <GlobalTaskCalendar />
          </ProtectedRoute>
        )
      },
      
      // Tasks section
      {
        path: 'tasks',
        children: [
          // Static routes first
          {
            index: true,
            element: (
              <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'read' }}>
                <TasksPage />
              </ProtectedRoute>
            )
          },
          {
            path: 'me',
            element: (
              <ProtectedRoute>
                <TasksPage />
              </ProtectedRoute>
            )
          },
          {
            path: 'create',
            element: (
              <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'update' }}>
                <DrawerTriggerPage mode="create" />
              </ProtectedRoute>
            )
          },
          // TaskList with taskId parameter - for opening the drawer directly
          {
            path: ':id',
            element: (
              <ProtectedRoute requiredPermission={{ resource: 'projects', action: 'read' }}>
                <TasksPage />
              </ProtectedRoute>
            )
          },
          // Redirect any other paths to tasks
          { path: '*', element: <Navigate to="/tasks" replace /> }
        ]
      },

      // Clients
      {
        path: 'clients',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'clients', action: 'read' }}>
            <Clients />
          </ProtectedRoute>
        )
      },

      // Include client routes
      ...clientRoutes.map(route => ({
        path: route.props.path,
        element: route.props.element
      })),

      // Brands
      {
        path: 'brands',
        children: [
          { index: true, element: <Brands /> },
          { path: 'create', element: <BrandForm /> },
          { path: ':id', element: <BrandDetail /> },
          { path: ':id/edit', element: <BrandForm /> }
        ]
      },

      // Client brands relationship
      {
        path: 'clients/:clientId/brands',
        element: <Brands />
      },

      // Campaigns
      {
        path: 'campaigns',
        children: [
          { index: true, element: <Campaigns /> },
          { path: 'create', element: <CampaignForm /> },
          { path: ':id', element: <CampaignDetail /> },
          { path: ':id/edit', element: <CampaignForm /> }
        ]
      },

      // Lead routes
      ...leadRoutes.map(route => ({
        path: route.props.path,
        element: route.props.element
      })),

      // Content routes
      ...contentRoutes.map(route => ({
        path: route.props.path,
        element: route.props.element
      })),

      // SOSTAC routes
      ...sostacRoutes.map(route => ({
        path: route.path,
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'sostac', action: 'read' }}>
            {route.element}
          </ProtectedRoute>
        )
      })),

      // Process Flow routes
      ...processFlowRoutes.map(route => ({
        path: route.props.path,
        element: route.props.element
      })),

      // User routes
      ...userRoutes.map(route => ({
        path: route.props.path,
        element: route.props.element
      })),

      // Invoice routes
      ...invoiceRoutes.map(route => ({
        path: route.props.path,
        element: route.props.element
      })),
      
      // Expense routes
      ...expenseRoutes.map(route => ({
        path: route.path,
        element: <ProtectedRoute requiredPermission={{ resource: 'expenses', action: route.permission.split('.')[1] }}>
          <route.element />
        </ProtectedRoute>
      })),

      // Pricing routes
      ...pricingRoutes,
      
      // Vendor routes
      ...vendorRoutes.map(route => ({
        path: route.path,
        element: <ProtectedRoute requiredPermission={{ resource: 'vendors', action: 'read' }}>
          <route.element />
        </ProtectedRoute>
      })),
      
      // Form routes
      ...formRoutes.map(route => ({
        path: route.props.path,
        element: route.props.element
      })),

      // Workflow Routes
      {
        path: 'workflows',
        children: [
          { index: true, element: <WorkflowTemplates /> },
          {
            path: 'templates',
            children: [
              { index: true, element: <WorkflowTemplates /> },
              { path: 'create', element: <WorkflowTemplateForm /> },
              { path: ':id', element: <WorkflowTemplateForm /> },
              { path: ':id/edit', element: <WorkflowTemplateForm /> },
              { path: ':id/duplicate', element: <WorkflowTemplateForm /> }
            ]
          },
          {
            path: 'instances',
            children: [
              { index: true, element: <WorkflowInstances /> },
              { path: 'create', element: <WorkflowCreate /> },
              { path: ':id', element: <WorkflowDetail /> },
              { path: ':id/advance', element: <WorkflowDetail /> },
              { path: ':id/reject', element: <WorkflowDetail /> }
            ]
          }
        ]
      },

      // Analytics Routes
      {
        path: 'analytics/*',
        element: <AnalyticsRoutes />
      },
      
      // Time Tracking Analytics Route
      {
        path: 'time-tracking-analytics',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'time_tracking_analytics', action: 'read' }}>
            <TimeTrackingAnalytics />
          </ProtectedRoute>
        )
      },
      
      // HR Routes
      ...hrRoutes,
      
      // Payroll Routes
      ...payrollRoutes.map(route => ({
        path: route.path,
        element: (
          <ProtectedRoute requiredPermission={route.meta?.permissions}>
            {route.element}
          </ProtectedRoute>
        )
      })),

      // PDF Routes (only admin routes, public routes are defined at root level)
      ...pdfRoutes.filter(route => {
        const path = route.props.path;
        return !(path === 'pdf/view/:id' || path === 'p/:shortCode' || path === 'docs/:slug');
      }).map(route => ({
        path: route.props.path,
        element: route.props.element
      })),

         // Admin routes - require specific permissions
         {
          path: 'admin/roles',
          element: (
            <ProtectedRoute
              requiredPermission={{ resource: 'roles', action: 'read' }}
            >
              <RolesPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'admin/roles/new',
          element: (
            <ProtectedRoute
              requiredPermission={{ resource: 'roles', action: 'create' }}
            >
              <CreateRolePage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'admin/roles/:roleId',
          element: (
            <ProtectedRoute
              requiredPermission={{ resource: 'roles', action: 'update' }}
            >
              <EditRolePage />
            </ProtectedRoute>
          ),
        },
    ]
  },

  // Auth routes
  {
    path: '/',
    element: <AuthLayout />,
    children: [
      { path: 'login', element: <Login /> },
      // { path: 'register', element: <Register /> },
      { path: 'forgot-password', element: <ForgotPassword /> },
      { path: 'reset-password', element: <ResetPassword /> }
    ]
  },

  // Public form routes - need to be outside the protected layout
  {
    path: 'forms/shared/:shareToken',
    element: <Suspense fallback={<div>Loading...</div>}>
      <PublicFormView />
    </Suspense>
  },
  {
    path: 'forms/shared/:shareToken/password',
    element: <Suspense fallback={<div>Loading...</div>}>
      <FormPasswordProtection />
    </Suspense>
  },
  {
    path: 'forms/shared/:shareToken/thank-you',
    element: <Suspense fallback={<div>Loading...</div>}>
      <ThankYouPage />
    </Suspense>
  },

  // Public PDF routes - need to be outside the protected layout
  ...pdfRoutes.filter(route => {
    const path = route.props.path;
    return path === 'pdf/view/:id' || path === 'p/:shortCode' || path === 'docs/:slug';
  }).map(route => ({
    path: route.props.path,
    element: route.props.element
  })),

  // Catch all route - redirect to dashboard
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />
  }
]);

export default router;